// Jul-2026 — Identity Audit Sweep
// Scans EVERY historical Bill Payment Voucher + Vendor Advance and flags
// rows whose FROZEN beneficiary bank details don't match the voucher's
// vendor identity or the current Vendor Master.
//
// Path: /reports/identity-audit
// Endpoints:
//   POST /api/reports/identity-sweep/summary       → rows + totals
//   POST /api/reports/identity-sweep/export.xlsx   → full account numbers

import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/ui-helpers";
import { Card } from "@/components/ui/card";
import {
  AlertTriangle, FileSpreadsheet, Filter, RotateCw, ShieldAlert, ShieldCheck,
} from "lucide-react";

const EMPTY = { from_date: "", to_date: "", branch_id: "", kind: "", severity: "", q: "", include_ok: false };
const selCls = "w-full border border-slate-300 px-2.5 py-2 text-xs bg-white focus:outline-none focus:border-orange-500";

const SEV_BADGE = {
  error: "bg-red-100 text-red-800 border-red-300",
  warning: "bg-amber-100 text-amber-800 border-amber-300",
  ok: "bg-emerald-100 text-emerald-800 border-emerald-300",
};
const FLAG_CHIP = {
  error: "bg-red-50 text-red-700 border-red-200",
  warning: "bg-amber-50 text-amber-700 border-amber-200",
};

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;

export default function IdentityAuditReport() {
  const [filters, setFilters] = useState(EMPTY);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [branches, setBranches] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/branches");
        setBranches(Array.isArray(r.data) ? r.data : r.data?.items || []);
      } catch { /* optional */ }
    })();
    run(EMPTY);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const body = (f) => ({
    from_date: f.from_date || "", to_date: f.to_date || "",
    branch_id: f.branch_id || "", kind: f.kind || "",
    severity: f.severity || "", q: f.q || "",
    include_ok: !!f.include_ok,
  });

  async function run(f = filters) {
    setLoading(true);
    try {
      const res = await api.post("/reports/identity-sweep/summary", body(f));
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to run the identity sweep");
    } finally { setLoading(false); }
  }

  async function doExport() {
    setExporting(true);
    try {
      const res = await api.post("/reports/identity-sweep/export.xlsx",
        body(filters), { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "identity_audit_sweep.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Excel export failed");
    } finally { setExporting(false); }
  }

  const rows = data?.rows || [];
  const t = data?.totals || {};
  const byCode = t.by_code || {};
  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  const cards = [
    { key: "scanned", label: "Vouchers Scanned", val: t.scanned, tone: "border-slate-300 bg-slate-100 text-slate-900" },
    { key: "errors", label: "Errors", val: t.errors, tone: "border-red-300 bg-red-50 text-red-900" },
    { key: "warnings", label: "Warnings", val: t.warnings, tone: "border-amber-300 bg-amber-50 text-amber-900" },
    { key: "clean", label: "Clean", val: t.clean, tone: "border-emerald-300 bg-emerald-50 text-emerald-900" },
    { key: "wrong_owner", label: "Wrong Owner", val: byCode.WRONG_OWNER || 0, tone: "border-red-300 bg-red-50 text-red-900" },
    { key: "code_mismatch", label: "Code Mismatch", val: (byCode.CODE_MISMATCH || 0) + (byCode.ID_MISMATCH || 0), tone: "border-red-300 bg-red-50 text-red-900" },
    { key: "no_snapshot", label: "No Snapshot", val: byCode.NO_SNAPSHOT || 0, tone: "border-orange-300 bg-orange-50 text-orange-900" },
    { key: "drift", label: "Bank Drift", val: (byCode.DRIFT_IFSC || 0) + (byCode.DRIFT_ACCOUNT || 0), tone: "border-amber-300 bg-amber-50 text-amber-900" },
  ];

  return (
    <div data-testid="identity-audit-page">
      <PageHeader
        eyebrow="Documents & Reports"
        title="Identity Audit Sweep"
        subtitle="Cross-checks the frozen beneficiary bank details on every past Bill Payment Voucher and Vendor Advance against the voucher's own vendor code and the current Vendor Master — surfacing misdirected, drifted or missing bank identities. Account numbers stay masked on screen; the Excel export carries full numbers."
        actions={
          <button data-testid="identity-export-xlsx" disabled={exporting || loading} onClick={doExport}
            className="bg-emerald-700 hover:bg-emerald-800 text-white px-4 py-2.5 text-xs uppercase tracking-wider font-bold flex items-center gap-2 disabled:opacity-50">
            <FileSpreadsheet size={14} /> {exporting ? "Exporting…" : "Excel"}
          </button>
        }
      />

      {/* Filters */}
      <Card className="mb-4">
        <div className="px-5 py-3 border-b border-slate-100">
          <h3 className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 flex items-center gap-2">
            <Filter size={12} /> Filters
          </h3>
        </div>
        <div className="p-4 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 items-end">
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">From Date</div>
            <input data-testid="identity-from-date" type="date" value={filters.from_date}
              onChange={(e) => set("from_date", e.target.value)} className={selCls} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">To Date</div>
            <input data-testid="identity-to-date" type="date" value={filters.to_date}
              onChange={(e) => set("to_date", e.target.value)} className={selCls} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Branch</div>
            <select data-testid="identity-branch" value={filters.branch_id}
              onChange={(e) => set("branch_id", e.target.value)} className={selCls}>
              <option value="">All Branches</option>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.branch_name || b.name}</option>)}
            </select>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Type</div>
            <select data-testid="identity-kind" value={filters.kind}
              onChange={(e) => set("kind", e.target.value)} className={selCls}>
              <option value="">BPVs + Advances</option>
              <option value="voucher">Bill Payment Vouchers</option>
              <option value="advance">Vendor Advances</option>
            </select>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Severity</div>
            <select data-testid="identity-severity" value={filters.severity}
              onChange={(e) => set("severity", e.target.value)} className={selCls}>
              <option value="">All Flagged</option>
              <option value="error">Errors only</option>
              <option value="warning">Warnings only</option>
            </select>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Search</div>
            <input data-testid="identity-search" type="text" placeholder="Voucher / vendor…" value={filters.q}
              onChange={(e) => set("q", e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && run()} className={selCls} />
          </div>
          <div className="flex gap-2">
            <button data-testid="identity-run" onClick={() => run()}
              className="flex-1 bg-[#F97316] hover:bg-[#EA580C] text-white px-3 py-2 text-xs uppercase tracking-wider font-bold flex items-center justify-center gap-1.5">
              <RotateCw size={12} className={loading ? "animate-spin" : ""} /> Run Sweep
            </button>
            <button data-testid="identity-reset" onClick={() => { setFilters(EMPTY); run(EMPTY); }}
              className="px-3 py-2 border border-slate-300 text-xs uppercase tracking-wider font-bold text-slate-600 hover:bg-slate-50">
              Reset
            </button>
          </div>
        </div>
        <div className="px-4 pb-3">
          <label className="inline-flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
            <input data-testid="identity-include-ok" type="checkbox" checked={!!filters.include_ok}
              onChange={(e) => { const f = { ...filters, include_ok: e.target.checked }; setFilters(f); run(f); }} />
            Also show clean rows
          </label>
        </div>
      </Card>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-4">
        {cards.map((c) => (
          <div key={c.key} data-testid={`identity-card-${c.key}`} className={`border px-4 py-3 ${c.tone}`}>
            <div className="text-[10px] uppercase tracking-wider font-bold opacity-70">{c.label}</div>
            <div className="text-2xl font-bold mt-0.5">{c.val ?? 0}</div>
          </div>
        ))}
      </div>

      {t.truncated && (
        <div className="mb-4 border border-amber-300 bg-amber-50 px-4 py-2 text-xs text-amber-900">
          Showing the first 5,000 rows — narrow the filters to see the rest.
        </div>
      )}

      {/* Flagged rows */}
      <Card>
        <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2">
          <ShieldAlert size={14} className="text-red-600" />
          <h3 className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">
            Flagged Vouchers ({rows.length})
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table data-testid="identity-audit-table" className="w-full text-xs">
            <thead>
              <tr className="bg-slate-50 text-left text-[10px] uppercase tracking-wider text-slate-500">
                <th className="px-4 py-2.5 font-bold">Severity</th>
                <th className="px-4 py-2.5 font-bold">Voucher No</th>
                <th className="px-4 py-2.5 font-bold">Type</th>
                <th className="px-4 py-2.5 font-bold">Date</th>
                <th className="px-4 py-2.5 font-bold">Status</th>
                <th className="px-4 py-2.5 font-bold">Branch</th>
                <th className="px-4 py-2.5 font-bold">Voucher Vendor</th>
                <th className="px-4 py-2.5 font-bold">Frozen Beneficiary</th>
                <th className="px-4 py-2.5 font-bold">Account / IFSC</th>
                <th className="px-4 py-2.5 font-bold text-right">Amount</th>
                <th className="px-4 py-2.5 font-bold">Findings</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={11} className="px-4 py-10 text-center text-slate-400">Running sweep…</td></tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={11} className="px-4 py-12 text-center text-slate-400">
                    <ShieldCheck size={28} className="mx-auto mb-2 text-emerald-500" />
                    No integrity issues found — every scanned voucher's frozen bank
                    details line up with its vendor code and the Vendor Master.
                  </td>
                </tr>
              ) : rows.map((r, i) => (
                <tr key={i} data-testid={`identity-row-${i}`}
                  className={`border-t border-slate-100 ${r.severity === "error" ? "bg-red-50/40 hover:bg-red-50" : r.severity === "warning" ? "bg-amber-50/30 hover:bg-amber-50" : "hover:bg-slate-50"}`}>
                  <td className="px-4 py-2.5 whitespace-nowrap">
                    <span className={`inline-block border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${SEV_BADGE[r.severity] || ""}`}>
                      {r.severity}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 whitespace-nowrap font-bold text-slate-900">{r.voucher_number || "—"}</td>
                  <td className="px-4 py-2.5 whitespace-nowrap">{r.kind === "voucher" ? "BPV" : "Advance"}</td>
                  <td className="px-4 py-2.5 whitespace-nowrap">{r.voucher_date || "—"}</td>
                  <td className="px-4 py-2.5 whitespace-nowrap">{r.status || "—"}</td>
                  <td className="px-4 py-2.5 whitespace-nowrap">{r.branch_name || "—"}</td>
                  <td className="px-4 py-2.5">
                    <div className="font-semibold text-slate-800">{r.vendor_code || "—"}</div>
                    <div className="text-slate-500">{r.vendor_name || ""}</div>
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="font-semibold text-slate-800">{r.beneficiary_vendor_code || "—"}</div>
                    <div className="text-slate-500">{r.beneficiary_vendor_name || ""}</div>
                  </td>
                  <td className="px-4 py-2.5 whitespace-nowrap">
                    <div className="font-mono text-[11px]">{r.account_number_masked || "—"}</div>
                    <div className="text-slate-500">{r.ifsc || ""}{r.bank_name ? ` · ${r.bank_name}` : ""}</div>
                  </td>
                  <td className="px-4 py-2.5 whitespace-nowrap text-right font-semibold">{inr(r.amount)}</td>
                  <td className="px-4 py-2.5">
                    <div className="flex flex-wrap gap-1">
                      {(r.flags || []).length === 0 ? (
                        <span className="text-emerald-700 text-[11px] font-semibold">Clean</span>
                      ) : r.flags.map((f, fi) => (
                        <span key={fi} title={f.message}
                          className={`inline-flex items-center gap-1 border px-1.5 py-0.5 text-[10px] font-bold ${FLAG_CHIP[f.severity] || ""}`}>
                          <AlertTriangle size={9} /> {f.label}
                        </span>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
